import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Magnetic } from "./primitives";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/solutions", label: "Solutions" },
  { to: "/products", label: "Products" },
  { to: "/network", label: "Our Network" },
  { to: "/work", label: "Work" },
  { to: "/sustainability", label: "Sustainability" },
  { to: "/insights", label: "Insights" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background,border,box-shadow] duration-500 ${
          scrolled ? "backdrop-blur-xl bg-white/80 border-b border-neutral-200 shadow-[0_8px_32px_rgba(0,0,0,0.04)]" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <img src="/brand/vetas-logo.png" alt="VETAS" className="h-7 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={`text-[13px] font-medium tracking-wide link-underline ${
                  pathname === n.to ? "text-neutral-900" : "text-neutral-600 hover:text-neutral-900"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Magnetic className="hidden sm:block">
              <Link
                to="/inquire"
                data-testid="nav-inquire-cta"
                className="group inline-flex items-center gap-1.5 rounded-full bg-[#f4b41a] hover:bg-[#d99e12] text-neutral-900 text-[13px] font-semibold px-5 py-2.5 transition-colors duration-300"
              >
                Inquire Now
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Magnetic>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-neutral-300 text-neutral-900"
              aria-label="Menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-white pt-[72px] lg:hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-1">
              {NAV.map((n, i) => (
                <motion.div key={n.to}
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}>
                  <Link to={n.to} className="font-display text-3xl font-medium py-2 block text-neutral-900">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <Link to="/inquire" data-testid="mobile-inquire-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#f4b41a] text-neutral-900 font-semibold px-6 py-4">
                Inquire Now <ArrowUpRight size={18} />
              </Link>
              <Link to="/contact" className="mt-2 text-center text-neutral-600 py-3">Contact</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
